import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, ShieldCheck, FileSearch, Database, HeartPulse, HeartOff, Zap, TrendingUp, Filter, X, Loader2, Globe, Smartphone } from "lucide-react";
import { api, faDigits, faNum } from "../lib/api";
import JalaliDateTimePicker from "../components/JalaliDateTimePicker";

const WINDOWS = [
  { key: "24h", label: "۲۴ ساعت اخیر" },
  { key: "3d", label: "۳ روز اخیر" },
  { key: "1w", label: "۱ هفته اخیر" },
  { key: "1m", label: "۱ ماه اخیر" },
  { key: "all", label: "از ابتدا" },
];

const OVERALL = [
  { key: "identity_cached", label: "کل هویت‌های کش‌شده", icon: Database, color: "indigo" },
  { key: "identity_alive", label: "افراد در قید حیات", icon: HeartPulse, color: "emerald" },
  { key: "identity_deceased", label: "افراد فوت‌شده", icon: HeartOff, color: "rose" },
  { key: "mobile_people", label: "افراد دارای شماره موبایل", icon: Smartphone, color: "teal" },
  { key: "mobile_total", label: "مجموع شماره موبایل‌ها", icon: Smartphone, color: "indigo" },
  { key: "mobile_matched_inquiries", label: "استعلام‌های شاهکار تطابق‌دار", icon: ShieldCheck, color: "emerald" },
  { key: "mobile_mismatch_inquiries", label: "استعلام‌های شاهکار عدم تطابق", icon: X, color: "rose" },
  { key: "accounts_enabled", label: "اکانت‌های فعال", icon: Users, color: "teal" },
  { key: "cookies_healthy", label: "کوکی سالم", icon: ShieldCheck, color: "emerald" },
  { key: "accounts_total", label: "کل اکانت‌ها", icon: Users, color: "slate" },
];

const WINDOWED = [
  { key: "inquiries_total", label: "کل استعلامات", icon: FileSearch, color: "teal" },
  { key: "inquiries_success", label: "استعلامات موفق", icon: Zap, color: "emerald" },
  { key: "success_rate", label: "نرخ موفقیت", icon: TrendingUp, color: "indigo", suffix: "٪" },
  { key: "cache_hits", label: "از کش پاسخ داده شد", icon: Database, color: "amber" },
  { key: "upstream_hits", label: "فراخوانی از عدل‌ایران", icon: FileSearch, color: "slate" },
];

const COLORS = {
  teal: "bg-teal-50 text-teal-700 border-teal-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

const SOURCE_CARDS = [
  { key: "cache", label: "کش", icon: Database, color: "amber" },
  { key: "upstream", label: "عدل‌ایران", icon: FileSearch, color: "teal" },
  { key: "online", label: "عدل‌ایران آنلاین", icon: Globe, color: "indigo" },
];

function RateBar({ rate, color = "teal" }) {
  const barColor = { teal: "bg-teal-500", amber: "bg-amber-500", indigo: "bg-indigo-500", emerald: "bg-emerald-500" }[color] || "bg-teal-500";
  return (
    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(100, Math.max(0, rate))}%` }} />
    </div>
  );
}

function Bucket({ b }) {
  if (!b) return null;
  return (
    <div className="flex items-center gap-1.5 text-[11px] tabular-nums whitespace-nowrap">
      <span className="text-slate-500">{faNum(b.total)}</span>
      <span className="text-emerald-600">{faNum(b.success)}✓</span>
      <span className="text-rose-500">{faNum(b.fail)}✗</span>
      <span className="text-indigo-600 font-semibold">{faDigits(b.success_rate)}٪</span>
    </div>
  );
}

function DetailedStats() {
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (f, t) => {
    setLoading(true);
    try {
      const params = {};
      if (f) params.start = Math.floor(f.getTime() / 1000);
      if (t) params.end = Math.floor(t.getTime() / 1000);
      const { data: res } = await api.get("/admin/stats/detailed", { params });
      setData(res);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(null, null); /* eslint-disable-next-line */ }, []);

  const applyFilter = () => { setIsFiltered(!!(from || to)); load(from, to); };
  const clearFilter = () => { setFrom(null); setTo(null); setIsFiltered(false); load(null, null); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-700">آمار تفصیلی استعلامات</h2>
        <div className="flex flex-wrap items-center gap-2">
          <JalaliDateTimePicker value={from} onChange={setFrom} placeholder="از تاریخ" testId="detailed-stats-from" />
          <span className="text-slate-300 text-xs">تا</span>
          <JalaliDateTimePicker value={to} onChange={setTo} placeholder="تا تاریخ" testId="detailed-stats-to" />
          <button data-testid="detailed-stats-apply" onClick={applyFilter}
            className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 shrink-0">
            <Filter size={13} /> اعمال
          </button>
          {isFiltered && (
            <button data-testid="detailed-stats-clear" onClick={clearFilter}
              className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg" title="پاک کردن فیلتر">
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="animate-spin inline" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200/80 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5"><FileSearch size={13} /> کل استعلامات</div>
              <div className="text-xl font-bold text-slate-900 tabular-nums mb-1.5">{faNum(data.overall.total)}</div>
              <RateBar rate={data.overall.success_rate} />
              <div className="text-[11px] text-slate-400 mt-1.5 tabular-nums">✓{faNum(data.overall.success)} · ✗{faNum(data.overall.fail)} · {faDigits(data.overall.success_rate)}٪</div>
            </div>
            {SOURCE_CARDS.map((s) => {
              const b = data[s.key];
              const Icon = s.icon;
              return (
                <div key={s.key} className="bg-white border border-slate-200/80 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5"><Icon size={13} /> {s.label}</div>
                  <div className="text-xl font-bold text-slate-900 tabular-nums mb-1.5">{faNum(b.total)}</div>
                  <RateBar rate={b.success_rate} color={s.color} />
                  <div className="text-[11px] text-slate-400 mt-1.5 tabular-nums">✓{faNum(b.success)} · ✗{faNum(b.fail)} · {faDigits(b.success_rate)}٪</div>
                </div>
              );
            })}
          </div>

          <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-600">مصرف به تفکیک اکانت</div>
            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead className="bg-slate-50 text-slate-500 font-medium">
                  <tr>
                    <th className="py-2 px-4 border-b font-medium">اکانت</th>
                    <th className="py-2 px-4 border-b font-medium">کد ملی</th>
                    <th className="py-2 px-4 border-b font-medium">عدل‌ایران</th>
                    <th className="py-2 px-4 border-b font-medium">عدل‌ایران آنلاین</th>
                    <th className="py-2 px-4 border-b font-medium">جمع کل</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-400">در این بازه مصرفی ثبت نشده</td></tr>
                  ) : data.accounts.map((a) => (
                    <tr key={a.account_id} className="row-hover border-b border-slate-100 last:border-0">
                      <td className="py-2.5 px-4 font-mono text-[11px]">{a.account_id}</td>
                      <td className="py-2.5 px-4 tabular-nums">{faDigits(a.national_code)}</td>
                      <td className="py-2.5 px-4"><Bucket b={a.upstream} /></td>
                      <td className="py-2.5 px-4"><Bucket b={a.online} /></td>
                      <td className="py-2.5 px-4 font-semibold"><Bucket b={{ total: a.total, success: a.success, fail: a.fail, success_rate: a.success_rate }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [window, setWindow] = useState("24h");
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stats", { params: { window } });
      setStats(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [window]);
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [window]);

  const render = (cards) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c, i) => {
        const Icon = c.icon;
        const value = stats?.[c.key] ?? 0;
        return (
          <motion.div key={c.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="stat-card bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
            data-testid={`stat-card-${c.key}`}>
            <div className={`w-10 h-10 rounded-xl grid place-items-center border ${COLORS[c.color]} mb-3`}><Icon size={18} /></div>
            <div className="text-sm text-slate-500 mb-1">{c.label}</div>
            <div className="text-2xl font-bold text-slate-900 tabular-nums">{faNum(value)}{c.suffix || ""}</div>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">داشبورد آمار</h1>
        <p className="text-sm text-slate-500 mt-1">نمای کلی از وضعیت سرویس در بازه‌های زمانی مختلف</p>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3">آمار کلی</h2>
        {render(OVERALL)}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-slate-700">آمار استعلامات</h2>
          <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-full p-1">
            {WINDOWS.map((w) => (
              <button key={w.key} data-testid={`window-${w.key}`} onClick={() => setWindow(w.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${window === w.key ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
                {w.label}
              </button>
            ))}
          </div>
        </div>
        {render(WINDOWED)}
      </div>

      <DetailedStats />
    </div>
  );
}
